import { parseISO } from 'date-fns'
import { Grid, TextField, Box, Typography } from '@mui/material'
import { DataGridPro } from '@mui/x-data-grid-pro'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useRef, useState, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { getLocalDateString } from '../../../utils/date-utils'
import Modal from '../../../shared/UI/Modal'
import InfoBanner from '../../../shared/UI/InfoBanner'
import DropdownMenu from '../../../shared/UI/DropdownMenu'
import Card from '../../../shared/layout/Card'
import NoDataFound from '../../../shared/UI/NoDataFound'
import LoadingIndicator from '../../../shared/UI/LoadingIndicator'
import { AbsenceDetailProps } from '../types'
import { deleteAbsenceDay } from '../functions'
import { AbsenceDay } from '../../../types/absence-day'
import { RootStore } from '../../../redux/store'
import {
  hideModal,
  SetShowModalPayload,
  showModalDialog,
} from '../../../redux/reducers/appSettingsReducer'
import { getSelectValuesByType } from '../../../utils/app-utils'
import { manualRequestsService } from '../../../services/myActionsService'
import SickHoursDetailPanel from './SickHoursDetailPanel'
import { createAbsenceDetailColumns } from './columnDefinitions'
import { createLegacyAbsenceDetailColumns } from './columnDefinitionsLegacy'
import { useDeleteFormValidation, useSickHoursValidation, useLocationState } from './hooks'
import { StyledAbsenceDetailGrid } from './components/StyledAbsenceDetailGrid'
import { StyledHeaderText } from './components/StyledHeaderText'
import { absenceFormLabels } from '../../../utils/constants'
import AbsenceLedgerModal from '../../AbsenceDrawerComponent/AbsenceLedgerModal'
import { useAbsenceLedger } from '../../AbsenceDrawerComponent/hooks/useAbsenceLedger'
import { AbsenceLedgerMode } from '../../../types/osp-ledger'
import { useIsSplitPotsForUk } from '../../../utils/Hooks/useIsSplitPotsForUk'
import { baseGridSx } from '../../AbsenceDrawerComponent/AbsenceLedgerModal/gridStyles'

const absenceDetailGridSx = {
  ...baseGridSx,
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#FFFFFF',
    minHeight: '48px !important',
    maxHeight: '48px !important',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
  },
  '& .MuiDataGrid-virtualScroller': {
    overflow: 'hidden',
    borderTop: '1px solid rgba(0,0,0,0.1)',
  },
  // Pinned actions column — remove MUI default shadow/border
  '& .MuiDataGrid-pinnedColumns': { boxShadow: 'none !important' },
  '& .MuiDataGrid-pinnedColumnsContainer': { boxShadow: 'none !important' },
  '& .MuiDataGrid-pinnedColumns--right': { borderLeft: '1px solid rgba(0,0,0,0.1) !important', boxShadow: 'none !important' },
  '& .MuiDataGrid-pinnedColumnHeaders--right': { borderLeft: '1px solid rgba(0,0,0,0.1) !important', borderBottom: '1px solid rgba(0,0,0,0.1) !important', boxShadow: 'none !important' },
  // Actions cell padding
  '& .MuiDataGrid-cell[data-field="actions"]': { padding: '0 8px', justifyContent: 'center' },
  // Row hover — override baseGridSx suppression; Mui-hovered is set on all row elements (incl. pinned) by DataGrid Pro
  '& .MuiDataGrid-row.Mui-hovered': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
  // Focus outlines
  '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': { outline: 'transparent' },
  '& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
}

export default function AbsenceDetailWithManAuth({
  absenceDays,
  absenceId,
  onEdit,
  onDelete,
  handleUpdate,
  isLoading,
  isData,
  viewOnly = false,
  handleViewClick,
  isPayrollApproverReadOnlyView = false,
  hasFutureSicknessAbsence = false,
  isRtwComplete,
  rtwRequired = true,
  absenceStatusType,
  absenceOccurrence
}: AbsenceDetailProps) {
  const [selectedRow, setSelectedRow] = useState<AbsenceDay | null>(null)
  const [showLedgerModal, setShowLedgerModal] = useState(false)
  const [hasSplitPots, setHasSplitPots] = useState<boolean>(false)

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const {
    deleteReason,
    deleteComment,
    disabled,
    validationErrors,
    setDeleteReason,
    setDeleteComment,
    validateForm,
    resetForm,
  } = useDeleteFormValidation()

  const { showModal, title, message, type, buttonLabel } = useSelector<
    RootStore,
    SetShowModalPayload
  >((state: RootStore) => state.appSettings.modalProps)
  const location = useLocation()
  const returnTo = (location.state as { previousLocation?: string } | null)?.previousLocation || '/myactions'

  const userPermissions = useSelector<RootStore, string[]>(
    (state: RootStore) => state.userState.permissions
  )

  const employeeDetails = useSelector(
    (state: RootStore) => state.appSettings.employeeDetails
  )
  const isSplitPotsForUk = useIsSplitPotsForUk(absenceOccurrence?.employeeResponse?.locationGroupName)
  const isManager = absenceOccurrence != null && absenceOccurrence.employeeResponse?.id !== employeeDetails?.employeeId

  const { absenceType } = absenceDays[0]
  const absenceReasonType = absenceType.reasonType
  const absenceTypeId = absenceType.absenceTypeId
  const isSickness = absenceReasonType?.toLowerCase() === 'sickness'

  const { sickHoursMap, validationErrorsMap, disabledMap, updateSickHours, isValidForRow } =
    useSickHoursValidation(absenceDays)

  const { location: legacyLocation } = useLocationState(absenceReasonType)

  const dateRange: [Date | null, Date | null] = useMemo(() => [
    absenceOccurrence?.startDate ? parseISO(absenceOccurrence.startDate) : null,
    absenceOccurrence?.endDate ? parseISO(absenceOccurrence.endDate) : null,
  ], [absenceOccurrence?.startDate, absenceOccurrence?.endDate])

  const { overviewRows, breakdownRows, isLoading: isLoadingLedger } = useAbsenceLedger({
    employeeId: absenceOccurrence?.employeeResponse?.id,
    dateRange,
    enabled: showLedgerModal && isSplitPotsForUk,
    extendAbsenceData: absenceOccurrence ?? undefined,
    mode: AbsenceLedgerMode.Historical,
  })

  useEffect(() => {
    if (!isSplitPotsForUk) return
    let cancelled = false
    const employeeId = absenceOccurrence?.employeeResponse?.id
    const startDate = dateRange[0]
    const endDate = dateRange[1]
    if (!employeeId || !startDate || !endDate) return
    manualRequestsService
      .getEmployeeAbsenceTransactionsAndOSPBalance(employeeId, startDate, endDate)
      .then(response => {
        if (!cancelled) setHasSplitPots(response.hasOsp50 ?? false)
      })
      .catch((err) => {
        if (!cancelled) {
          console.error(err)
          setHasSplitPots(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [isSplitPotsForUk, absenceOccurrence?.employeeResponse?.id, dateRange])

  const isModalButtonDisabled = useMemo(
    () => disabled || hasFutureSicknessAbsence,
    [disabled, hasFutureSicknessAbsence]
  )

  const didMountRef = useRef(false)

  const handleDeleteModal = (row: AbsenceDay) => {
    setSelectedRow(row)
  }

  const submitRequest = () =>
    deleteAbsenceDay(
      selectedRow,
      absenceId,
      deleteReason,
      deleteComment,
      absenceDays,
      manualRequestsService,
      dispatch,
      onDelete,
      () => resetForm(),
      navigate,
      returnTo
    )

  useEffect(() => {
    if (didMountRef.current && selectedRow) {
      dispatch(
        showModalDialog({
          title: 'Are you sure?',
          message: `Are you sure you would like to delete this absence ${
            absenceDays.length > 1 ? 'day' : 'occurrence'
          } for the ${selectedRow && getLocalDateString(selectedRow.date)}?`,
          buttonLabel: 'Delete',
          type: 'question',
          showModal: true,
          isDisabled: disabled,
        })
      )
    }
    didMountRef.current = true
  }, [selectedRow, disabled, dispatch, absenceDays])

  const newRows = absenceDays?.map(day => ({ ...day })) || []

  const hasManAuthRequest = absenceDays.some(day => day.payApproval && day.payApproval > 0)

  function headerText(testId: string, headerName?: string) {
    return <StyledHeaderText testId={testId} headerName={headerName} />
  }

  const getColumnTestId = (columnName: string): string => `Absence-Detail-Table-${columnName}`

  const getDetailPanelContent = (params: any) => {
    const row = absenceDays.find(day => day.id === params.row.id)
    if (!row) return null

    const sickHours = sickHoursMap.get(row.id) || 0
    const isDisabled = disabledMap.get(row.id) || false
    const rowValidationErrors = validationErrorsMap.get(row.id) || {}

    return (
      <SickHoursDetailPanel
        row={row}
        location={legacyLocation}
        sickHours={sickHours}
        validationErrors={rowValidationErrors}
        isDisabled={isDisabled}
        onSickHoursChange={updateSickHours}
        onValidation={isValidForRow}
        onUpdate={handleUpdate}
      />
    )
  }

  const getDetailPanelHeight = () => 160

  const columns = isSplitPotsForUk
    ? createAbsenceDetailColumns({
        isSickness,
        onEdit,
        onDelete: handleDeleteModal,
        userPermissions,
        viewOnly,
        handleViewClick,
        isPayrollApproverReadOnlyView,
        isRtwComplete,
        rtwRequired,
        absenceStatusType,
        absenceTypeId,
        isManager,
      })
    : createLegacyAbsenceDetailColumns({
        hasManAuthRequest,
        isSickness,
        onEdit,
        onDelete: handleDeleteModal,
        headerText,
        getColumnTestId,
        userPermissions,
        viewOnly,
        handleViewClick,
        isPayrollApproverReadOnlyView,
        isRtwComplete,
        rtwRequired,
        absenceStatusType,
        absenceTypeId,
      })

  return (
    <>
      <Grid container>
        <Modal
          type={type}
          open={showModal}
          onClose={() => {
            setSelectedRow(null)
            dispatch(hideModal())
          }}
          onClick={() => {
            if (validateForm()) {
              setSelectedRow(null)
              submitRequest()
              dispatch(hideModal())
            }
          }}
          title={title}
          message={message}
          buttonLabel={buttonLabel}
          isDisabled={isModalButtonDisabled}
          maxWidth="xl"
          banner={
            hasFutureSicknessAbsence && isSickness ? (
              <InfoBanner
                dataTestId="future-sickness-warning-modal"
                margins={{ left: '0', right: '0', top: '16px', bottom: '0' }}
                text={absenceFormLabels.deleteAbsenceDayFutureSicknessAbsenceWarning}
                sx={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  maxWidth: '100%',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              />
            ) : undefined
          }
          dropdown={
            <Grid container>
              <Grid item xs={12} md={12} sx={{ mt: 4 }}>
                <DropdownMenu
                  label="Reason"
                  id="reason"
                  data={getSelectValuesByType('AbsenceDeletionReason')}
                  textField="displayValue"
                  valueField="value"
                  onChange={e => {
                    const reasonStr = getSelectValuesByType('AbsenceDeletionReason').find(
                      x => x.value === Number(e.target.value)
                    )?.displayValue
                    setDeleteReason(reasonStr!)
                  }}
                  error={validationErrors.deleteReason}
                />
                <Grid item sx={{ mt: 2 }}>
                  <TextField
                    label="Comments"
                    value={deleteComment}
                    fullWidth
                    multiline
                    rows={4}
                    onChange={e => {
                      setDeleteComment(e.target.value)
                    }}
                    error={validationErrors.deleteComment}
                  />
                </Grid>
              </Grid>
            </Grid>
          }
        />
      </Grid>
      {isSplitPotsForUk ? (
        <Box
          sx={{
            borderRadius: '16px',
            border: '1px solid rgba(0,0,0,0.08)',
            backgroundColor: '#FFFFFF',
            overflow: 'hidden',
          }}
        >
          {/* Title row — matches Absence Ledger modal DialogTitle area */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '24px',
              borderBottom: '1px solid rgba(0,0,0,0.08)',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 500,
                fontSize: '17px',
                lineHeight: '17px',
                letterSpacing: 0,
                color: '#000000',
              }}
            >
              {`Absence Detail for ${absenceType.reasonTypeDescription}`}
            </Typography>
            {isSickness && (
              <Box
                component="button"
                onClick={() => setShowLedgerModal(true)}
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '20px',
                  letterSpacing: '0.15px',
                  color: '#3C4A44',
                  backgroundColor: 'transparent',
                  border: '1px solid #BBCAC3',
                  borderRadius: '100px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  outline: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(60, 74, 68, 0.04)',
                  },
                }}
              >
                More detail
              </Box>
            )}
          </Box>

          {/* Grid content */}
          <Box sx={{ padding: '24px' }}>
            <DataGridPro
              rows={newRows}
              columns={columns}
              disableSelectionOnClick
              disableColumnFilter
              disableColumnMenu
              disableColumnReorder
              disableColumnResize
              autoHeight
              pagination={false}
              hideFooter
              getRowId={row => row.id}
              rowHeight={40}
              headerHeight={48}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'date', sort: 'asc' }],
                },
                pinnedColumns: {
                  right: ['actions'],
                },
              }}
              sx={absenceDetailGridSx}
            />
            {!isLoading ? (
              <>
                {!isData && (
                  <Grid item xs={12} sx={{ mt: 4 }}>
                    <NoDataFound show />
                  </Grid>
                )}
              </>
            ) : (
              <Grid item xs={12} sx={{ mt: 4 }}>
                <LoadingIndicator show />
              </Grid>
            )}
          </Box>
        </Box>
      ) : (
        <Card title={`Absence Detail for ${absenceType.reasonTypeDescription}`}>
          <StyledAbsenceDetailGrid
            rows={newRows}
            columns={columns}
            disableSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            disableColumnReorder
            autoHeight
            pagination={false}
            hideFooter
            getRowId={row => row.id}
            {...(!hasManAuthRequest &&
              isSickness && {
                getDetailPanelContent,
                getDetailPanelHeight,
              })}
            initialState={{
              sorting: {
                sortModel: [{ field: 'date', sort: 'asc' }],
              },
              pinnedColumns: {
                right: ['edit', 'delete'],
              },
            }}
          />

          {!isLoading ? (
            <>
              {!isData && (
                <Grid item xs={12} sx={{ mt: 4 }}>
                  <NoDataFound show />
                </Grid>
              )}
            </>
          ) : (
            <Grid item xs={12} sx={{ mt: 4 }}>
              <LoadingIndicator show />
            </Grid>
          )}
        </Card>
      )}
      {isSplitPotsForUk && (
        <AbsenceLedgerModal
          open={showLedgerModal}
          onClose={() => setShowLedgerModal(false)}
          overviewRows={overviewRows}
          breakdownRows={breakdownRows}
          loading={isLoadingLedger}
          hasSplitPots={hasSplitPots}
          showDiscretionaryPay={isManager}
        />
      )}
    </>
  )
}
